import React, { useState } from "react";
import { api, errMsg, fmtDate, fmtDateTime, toPaise, fromPaise } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { Money, MoneyInput, StoreDatePicker, EmptyState, LoadErrorState, LoadingState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SendHorizontal, Lock, Pencil, Landmark, Calculator, Wrench, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const istTime = (iso) => new Date(iso).toLocaleTimeString("en-IN", {
  timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit",
});

export default function SendHQPage() {
  const { user, storeId } = useApp();
  const isCashier = user.role === "cashier";
  const isAdmin = user.role === "admin";
  const effStore = isCashier ? user.store_id : storeId;

  const [dialog, setDialog] = useState(null); // { transfer|null, amount, note, close, reason }
  const [fix, setFix] = useState(null);       // admin correction { transfer, amount, reason }
  const [busy, setBusy] = useState(false);

  const q = useAsyncData(
    () => {
      if (!effStore) return Promise.resolve(null);
      return api.get("/hq/statement", { params: { store_id: effStore } }).then((r) => r.data);
    },
    [effStore]
  );
  const data = q.data;

  const openSend = () => setDialog({
    transfer: null, close: data.sendable.business_date,
    amount: fromPaise(Math.max(data.sendable.remaining_paise, 0)), note: "", reason: "",
  });
  const openEdit = (t) => setDialog({
    transfer: t, close: t.source_close_business_date,
    amount: fromPaise(t.amount_paise), note: t.note || "", reason: "",
  });

  const closeMeta = (bd) => (data?.closes || []).find((c) => c.business_date === bd);
  const needsReason = !!dialog && !dialog.transfer &&
    (dialog.close !== data?.sendable?.business_date || !!data?.auto_link_blocked);

  const save = async () => {
    const amountP = toPaise(dialog.amount);
    if (!amountP || amountP <= 0) { toast.error("Enter the amount to send"); return; }
    setBusy(true);
    try {
      if (dialog.transfer) {
        await api.put(`/hq/transfers/${dialog.transfer.id}`, {
          amount_paise: amountP, note: dialog.note || null, version: dialog.transfer.version,
        });
        toast.success("HQ entry updated");
      } else {
        await api.post("/hq/transfers", {
          store_id: effStore, amount_paise: amountP, note: dialog.note || null,
          source_close_business_date: dialog.close,
          reason: dialog.reason || null,
        });
        toast.success(`Sent to HQ from the close of ${fmtDate(dialog.close)}`);
      }
      setDialog(null);
      q.refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const cancelEntry = async () => {
    setBusy(true);
    try {
      await api.post(`/hq/transfers/${dialog.transfer.id}/void`, { reason: "Cancelled from Send to HQ page" });
      toast.success("HQ entry cancelled");
      setDialog(null);
      q.refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const saveFix = async (voidIt = false) => {
    if (!fix.reason.trim()) { toast.error("A reason is compulsory"); return; }
    setBusy(true);
    try {
      await api.post(`/hq/transfers/${fix.transfer.id}/correct`, {
        reason: fix.reason, void: voidIt,
        amount_paise: voidIt ? null : toPaise(fix.amount),
      });
      toast.success(voidIt ? "Entry cancelled with an audited reason" : "Entry corrected (audited)");
      setFix(null);
      q.refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-xl font-semibold arch-underline">To Send to HQ</h1>
          <p className="text-sm text-muted-foreground">Branch cash statement — cash goes out of a <b>counted</b> close, same evening or next morning</p>
        </div>
        {!isCashier && <StoreDatePicker hideDate />}
      </div>

      {q.error ? (
        <LoadErrorState error={q.error} onRetry={q.reload} title="Could not load the branch statement" />
      ) : q.loading || !data ? (
        <LoadingState />
      ) : (
        <>
          <Card className="rounded-lg">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Total cash with branch</p>
                <p className="text-3xl font-semibold glow-sapphire mt-0.5" data-testid="hq-balance">
                  <Money paise={data.balance_paise} />
                </p>
                {data.sendable ? (
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="hq-sendable-note">
                    Available from the counted close of <b>{fmtDate(data.sendable.business_date)}</b>:{" "}
                    <Money paise={data.sendable.remaining_paise} className="text-foreground" />
                    {data.sendable.sent_paise > 0 && <> (already sent <Money paise={data.sendable.sent_paise} />)</>}
                    {!data.sendable.finalized && " — that close is not finalized yet, sending is still allowed"}
                  </p>
                ) : (
                  <p className="text-[11px] text-[hsl(var(--warning))] mt-1" data-testid="hq-count-required-note">
                    Record the closing cash count first — HQ cash is always sent out of a counted close.
                  </p>
                )}
                {data.provisional && (
                  <p className="text-[11px] text-[hsl(var(--warning))] mt-1" data-testid="hq-provisional-note">
                    Today's row is provisional (expected cash, not counted yet)
                  </p>
                )}
                {data.auto_link_blocked && (
                  <p className="text-[11px] text-[hsl(var(--warning))] mt-1" data-testid="hq-ambiguous-note">
                    {isAdmin
                      ? `Today's cash entries have already started — choose the close this cash came from and give a reason (audited).`
                      : `Today's cash entries have already started, so this cash can no longer be linked automatically to the close of ${fmtDate(data.sendable?.business_date)}. Ask the admin to record it against the correct close with a reason.`}
                  </p>
                )}
              </div>
              {data.can_send && (
                data.needs_admin_resolution ? (
                  <Button variant="outline" disabled className="h-11 shrink-0" data-testid="hq-blocked-button">
                    <AlertTriangle className="h-4 w-4 mr-1.5" />Admin must resolve the close
                  </Button>
                ) : data.sendable && data.sendable.remaining_paise > 0 ? (
                  <Button onClick={openSend}
                    className="h-11 bg-primary hover:bg-[hsl(var(--sapphire-2))] font-semibold shrink-0"
                    data-testid="hq-send-button">
                    <SendHorizontal className="h-4 w-4 mr-1.5" /> Send to HQ
                  </Button>
                ) : (
                  <Button asChild variant="outline" className="h-11 shrink-0" data-testid="hq-goto-count-button">
                    <a href="/cash-count"><Calculator className="h-4 w-4 mr-1.5" />
                      {data.sendable ? "Nothing left from this close" : "Submit closing count"}</a>
                  </Button>
                )
              )}
            </CardContent>
          </Card>

          <Card className="rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="table-compact">
                <TableHeader><TableRow>
                  <TableHead>Close date</TableHead>
                  <TableHead>Particulars</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.length === 0 && (
                    <TableRow><TableCell colSpan={6}>
                      <EmptyState icon={Landmark} title="No cash movements yet" sub="Counted closes and HQ sends will appear here" />
                    </TableCell></TableRow>
                  )}
                  {data.rows.map((r, i) => {
                    const isTransfer = r.kind === "transfer";
                    const t = r.transfer;
                    return (
                      <TableRow key={i} className={r.provisional ? "bg-[hsl(var(--warning))]/5" : r.uncounted ? "bg-[hsl(var(--danger))]/5" : ""}
                        data-testid={`hq-row-${i}`}>
                        <TableCell className="font-mono-num text-xs whitespace-nowrap">{fmtDate(r.business_date)}</TableCell>
                        <TableCell>
                          <span className={`text-sm font-medium ${isTransfer ? "text-[hsl(var(--danger))]" : ""}`}>
                            {r.label}
                          </span>
                          {isTransfer && (
                            <span className="block text-[11px] text-muted-foreground" data-testid={`hq-entered-${i}`}>
                              entered {fmtDateTime(t.entered_at)}{r.by ? ` by ${r.by}` : ""}
                            </span>
                          )}
                          {!isTransfer && r.by && <span className="block text-[11px] text-muted-foreground">by {r.by}</span>}
                          {r.note && <span className="block text-[11px] text-muted-foreground italic">{r.note}</span>}
                          {r.provisional && <span className="block text-[10px] text-[hsl(var(--warning))]">updates until the closing count is recorded</span>}
                          {r.uncounted && <span className="block text-[10px] text-[hsl(var(--danger))]">no closing count — this day is not part of the carry</span>}
                        </TableCell>
                        <TableCell className="amount-cell">
                          {r.credit_paise ? <Money paise={r.credit_paise} className="text-[hsl(var(--success))]" /> : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="amount-cell">
                          {r.debit_paise ? <Money paise={-r.debit_paise} className="text-[hsl(var(--danger))] font-semibold" /> : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="amount-cell font-semibold"><Money paise={r.balance_paise} /></TableCell>
                        <TableCell>
                          {isTransfer && (t.editable ? (
                            <div className="flex flex-col items-end gap-0.5">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)}
                                aria-label="Edit HQ entry" title="Edit HQ entry" data-testid={`hq-edit-${i}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap">until {istTime(t.editable_until)}</span>
                            </div>
                          ) : (
                            <div className="flex justify-end items-center gap-1">
                              <Lock className="h-3.5 w-3.5 text-muted-foreground" title="Locked — edit window passed" data-testid={`hq-locked-${i}`} />
                              {isAdmin && t.status === "active" && (
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={() => setFix({ transfer: t, amount: fromPaise(t.amount_paise), reason: "" })}
                                  aria-label="Admin correction" title="Admin correction (audited)"
                                  data-testid={`hq-correct-${i}`}>
                                  <Wrench className="h-3.5 w-3.5 text-[hsl(var(--brass))]" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
          <p className="text-[11px] text-muted-foreground">
            A send never changes the expected cash or variance of the day it came from — it only reduces the opening of the next day.
            Entries stay editable for 1 hour after saving, then lock; an admin can still post an audited correction.
          </p>
        </>
      )}

      {/* Send / edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="hq-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.transfer ? "Edit HQ entry" : "Send cash to HQ"}</DialogTitle>
            <DialogDescription className="text-xs">
              Cash always leaves a counted close. The send reduces the next day's opening — never the expected cash or variance of the day it came from.
            </DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              {!dialog.transfer && (
                <div className="space-y-1.5">
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Cash is coming out of this counted close</p>
                  {isAdmin ? (
                    <Select value={dialog.close} onValueChange={(v) => setDialog({
                      ...dialog, close: v, amount: fromPaise(Math.max(closeMeta(v)?.remaining_paise || 0, 0)),
                    })}>
                      <SelectTrigger data-testid="hq-close-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(data?.closes || []).map((c) => (
                          <SelectItem key={c.business_date} value={c.business_date}>
                            {fmtDate(c.business_date)} — counted {(c.counted_paise / 100).toLocaleString("en-IN")}, left {(c.remaining_paise / 100).toLocaleString("en-IN")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-semibold" data-testid="hq-close-label">
                      Close of {fmtDate(dialog.close)} · <Money paise={closeMeta(dialog.close)?.remaining_paise ?? 0} /> left
                    </p>
                  )}
                  {(dialog.close !== data?.sendable?.business_date || data?.auto_link_blocked) && (
                    <div className="rounded border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-2 space-y-1.5" data-testid="hq-backdate-warning">
                      <p className="text-[11px] text-[hsl(var(--warning))] flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        {dialog.close !== data?.sendable?.business_date
                          ? <>This is not the latest counted close ({fmtDate(data?.sendable?.business_date)}). A reason is compulsory and the entry is audited.</>
                          : <>Today's cash entries have already begun, so the source close must be confirmed. A reason is compulsory and the entry is audited.</>}
                      </p>
                      <Textarea value={dialog.reason} onChange={(e) => setDialog({ ...dialog, reason: e.target.value })}
                        placeholder="Why is this send linked to this close?" data-testid="hq-reason-input" />
                      {!dialog.reason.trim() && (
                        <p className="text-[11px] text-[hsl(var(--danger))]" data-testid="hq-reason-required">
                          A reason is needed before this send can be saved.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <p className="text-sm text-muted-foreground">
                  {dialog.transfer
                    ? <>Editable until <b>{istTime(dialog.transfer.editable_until)}</b> — after that an admin correction is needed.</>
                    : <>Prefilled with everything left from that close — edit if you are sending only a part. Multiple part-sends are fine.</>}
                </p>
                <MoneyInput value={dialog.amount} onChange={(v) => setDialog({ ...dialog, amount: v })}
                  className="h-12 text-lg" testId="hq-amount-input" autoFocus />
              </div>
              <Textarea value={dialog.note} onChange={(e) => setDialog({ ...dialog, note: e.target.value })}
                placeholder="Note (optional) — e.g. sent with Ramesh, sealed bag #4" data-testid="hq-note-input" />
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {dialog?.transfer && (
              <Button variant="outline" onClick={cancelEntry} disabled={busy}
                className="border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))]"
                data-testid="hq-void-button">
                Cancel this entry
              </Button>
            )}
            <Button onClick={save} disabled={busy || (needsReason && !dialog?.reason.trim())}
              className="bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="hq-save-button">
              {busy ? "Saving…" : dialog?.transfer ? "Update entry" : "Save — send to HQ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin correction dialog */}
      <Dialog open={!!fix} onOpenChange={(o) => !o && setFix(null)}>
        <DialogContent data-testid="hq-correct-dialog">
          <DialogHeader><DialogTitle>Admin correction (audited)</DialogTitle>
            <DialogDescription className="text-xs">
              Corrects a locked HQ entry. The reason is recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          {fix && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Entry of <Money paise={fix.transfer.amount_paise} className="text-foreground" /> from the close of{" "}
                <b>{fmtDate(fix.transfer.source_close_business_date)}</b>, entered {fmtDateTime(fix.transfer.entered_at)}.
                Corrections re-derive every later open day's opening; finalized days are flagged, never rewritten.
              </p>
              <MoneyInput value={fix.amount} onChange={(v) => setFix({ ...fix, amount: v })}
                className="h-11" testId="hq-correct-amount-input" />
              <Textarea value={fix.reason} onChange={(e) => setFix({ ...fix, reason: e.target.value })}
                placeholder="Reason (compulsory)" data-testid="hq-correct-reason-input" />
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => saveFix(true)} disabled={busy || !fix?.reason.trim()}
              className="border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))]" data-testid="hq-correct-void-button">
              Cancel the entry
            </Button>
            <Button onClick={() => saveFix(false)} disabled={busy || !fix?.reason.trim()}
              className="bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="hq-correct-save-button">
              Save correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
